import React, { useEffect, useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, CircularProgress, Alert, Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { stripePromise } from '@/util/stripe';
import { getBuyPanelsPath } from '../route';

interface StripePaymentFormProps {
    orderId: string;
    onSuccess: () => void;
    onCancel: () => void;
    paymentMethod?: string;
    beforePayment?: () => Promise<boolean>;
}

const CheckoutForm = ({ onSuccess, onCancel, amount, orderId, beforePayment }: { onSuccess: () => void, onCancel: () => void, amount: number, orderId: string, beforePayment?: () => Promise<boolean> }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { enqueueSnackbar } = useSnackbar();
    const routePrefix = getBuyPanelsPath(window.location.pathname, '');

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (beforePayment) {
            const canProceed = await beforePayment();
            if (!canProceed) {
                return;
            }
        }

        if (!stripe || !elements) {
            return; // Stripe.js has not yet loaded.
        }

        setIsLoading(true);
        setErrorMessage(null);

        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}${routePrefix}/payment-return?order=${encodeURIComponent(orderId)}`,
            },
        });

        if (error) {
            setErrorMessage(error.message || "An unexpected error occurred.");
            setIsLoading(false);
        } else {
            enqueueSnackbar("Payment successful! Please wait...", { variant: 'success' });
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 2 }}>
                <PaymentElement />
            </Box>

            {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button onClick={onCancel} disabled={isLoading} variant="outlined">
                    Cancel
                </Button>
                <Button 
                    type="submit" 
                    variant="contained" 
                    disabled={!stripe || isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isLoading ? 'Processing...' : 'Pay Now'}
                </Button>
            </Box>
        </form>
    );
};

export const StripePaymentForm: React.FC<StripePaymentFormProps> = ({ orderId, onSuccess, onCancel, paymentMethod, beforePayment }) => {
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [amount, setAmount] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setClientSecret(null);
        setAmount(0);
        setError(null);

        const fetchPaymentIntent = async () => {
            try {
                // Search for Cognito tokens (keys ending in idToken or accessToken)
                let token = "";
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && (key.endsWith('idToken') || key.endsWith('accessToken'))) {
                        token = localStorage.getItem(key) || "";
                        break;
                    }
                }

                // Fallback for manual testing or non-Cognito setups
                if (!token) {
                    token = localStorage.getItem('token') || 
                            localStorage.getItem('access_token') || 
                            localStorage.getItem('id_token') || "";
                }

                // DEBUG: Check console to verify token is found
                console.log("Stripe Auth Token found:", token ? "Yes (Starts with " + token.substring(0, 10) + ")" : "No");

                if (!token) {
                    setError("Session missing. Please logout and login again.");
                    return;
                }

                const payload: any = {
                    orderUuid: orderId,
                    paymentMethod: paymentMethod ?? (window as any).selectedPaymentMethod ?? 'payto'
                };

                const response = await fetch(`/api/orders/new/${orderId}/action/payment/stripe/create`, {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token.replace(/"/g, '')}` // Remove extra quotes
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const text = await response.text().catch(() => '<no body>');
                    console.error(`Backend returned ${response.status}: ${text}`);
                    throw new Error('Failed to initialize payment');
                }

                const data = await response.json();
                setClientSecret(data.clientSecret);
                setAmount(data.amount);
            } catch (err) {
                console.error(err);
                setError("Could not load secure payment form.");
            }
        };

        fetchPaymentIntent();
    }, [orderId, paymentMethod]);

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!clientSecret) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Elements 
            stripe={stripePromise} 
            options={{ 
                clientSecret, 
                appearance: {
                    theme: 'stripe',
                    variables: {
                        colorPrimary: '#0ea5a1',
                        colorBackground: '#0b1220',
                        colorText: '#ffffff',
                        colorDanger: '#ff6b6b',
                        fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
                        spacingUnit: '6px',
                        borderRadius: '6px',
                    },
                    rules: {
                        '.Label': { color: '#9CA3AF' },
                        '.Input': { backgroundColor: '#0f172000', color: '#fff', border: '1px solid #374151' },
                        '.Input:focus': { boxShadow: '0 0 0 3px rgba(38, 44, 43, 0.12)' },
                        '.Block': { backgroundColor: 'transparent' }
                    }
                }
            }}
        >
            <CheckoutForm 
                onSuccess={onSuccess} 
                onCancel={onCancel} 
                amount={amount} 
                orderId={orderId}
                beforePayment={beforePayment}
            />
        </Elements>
    );
};

export default StripePaymentForm;
